import { useState, useRef, useEffect, useCallback } from 'react';

import { debounce } from '../01-core/Utilities';
import { Swipeable } from '../01-core/Swipeable';
import { Media, MediaProps } from '../02-atoms/Media';
import { SliderNavigation } from '../02-atoms/SliderNavigation';

export interface GalleryProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The gallery items */
  items: MediaProps[];
  /** Whether to display the gallery as a slider */
  slider?: boolean;
  /** Additional class */
  className?: string;
  /** Optional label for previous page */
  prevLabel?: string;
  /** Optional label for next page */
  nextLabel?: string;
  /** Optional label for swipe indicator */
  swipeLabel?: string;
}

/** A block rendering a gallery */
export const Gallery = ({
  items,
  slider = false,
  prevLabel,
  nextLabel,
  swipeLabel,
  className,
  ...props
}: GalleryProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const tape = useRef<HTMLUListElement | null>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [pageOffsets, setPageOffsets] = useState<number[]>([0]);
  const [page, setPage] = useState<number>(0);
  const convertedItems = items.map((item) => {
    item.aspect = item.aspect || 'landscape';
    return item;
  });

  const calculatePages = useCallback(() => {
    if (!tape.current || !ref.current) return;

    const calculated = [];
    const offsets = [];
    const width = ref.current.getBoundingClientRect().width;
    const diff = tape.current.getBoundingClientRect().left;
    let max = width;
    let current = 1;

    const itemElements = Array.from(
      tape.current.querySelectorAll<HTMLElement>('.gallery__image'),
    );

    calculated.push(current.toString());
    offsets.push(0);
    itemElements.forEach((_, index) => {
      const rect = itemElements[index].getBoundingClientRect();
      if (rect.right - diff > max) {
        current = current + 1;
        calculated.push(current.toString());
        offsets.push(rect.left - diff);
        max = rect.left + width - diff;
      }
      if (index == itemElements.length - 1) {
        offsets[offsets.length - 1] = rect.right - diff - width;
      }
    });
    setPages(calculated as []);
    setPageOffsets(offsets);
  }, [tape, ref]);

  const swipeTo = (direction: string) => {
    if (direction == 'next') {
      slideTo(Math.min(pages.length - 1, page + 1));
    } else if (direction == 'prev') {
      slideTo(Math.max(0, page - 1));
    }
  };

  const slideTo = (index: number) => {
    if (!tape.current || !ref.current) return;
    const clamped = Math.max(0, Math.min(index, pageOffsets.length - 1));
    const offset = pageOffsets[clamped] ?? 0;
    setPage(clamped);
    tape.current.style.setProperty('--offset', -offset + 'px');
  };

  const handleResize = debounce(
    () => {
      calculatePages();
    },
    100,
    false,
  );

  useEffect(() => {
    if (!slider) {
      return;
    }
    window.addEventListener('resize', handleResize);
    window.requestAnimationFrame(() => {
      calculatePages();
    });
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [slider, handleResize, calculatePages, items.length]);

  return (
    <Swipeable
      ref={ref}
      onSwipeLeft={slider ? () => swipeTo('next') : undefined}
      onSwipeRight={slider ? () => swipeTo('prev') : undefined}
      className={[
        'gallery',
        slider ? 'gallery--slider' : 'gallery--grid',
        className,
      ].join(' ')}
      {...props}
    >
      <ul ref={tape} className="gallery__tape">
        {convertedItems.map((item, i) => {
          return (
            <li
              key={i}
              className={`gallery__image gallery__image--${item.aspect}`}
            >
              <Media {...item} />
            </li>
          );
        })}
      </ul>
      {slider && pages.length > 1 && (
        <SliderNavigation
          pages={pages}
          page={page}
          swipe={true}
          prevLabel={prevLabel}
          nextLabel={nextLabel}
          swipeLabel={swipeLabel}
          onChange={slideTo}
          className="gallery__navigation"
        />
      )}
    </Swipeable>
  );
};
